/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Send, Clock } from 'lucide-react';

export const Contacts = ({title}) => {
  const [formStatus, setFormStatus] = useState(null);

  useEffect(() => { document.title = title; }, [title]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);
    try {
      const response = await fetch("https://formspree.io/f/myzypawe", {
        method: "POST", body: data, headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        setFormStatus({ type: 'success', message: 'Thanks for your submission!' });
        form.reset();
      } else {
        setFormStatus({ type: 'error', message: 'Oops! There was a problem submitting your form' });
      }
    } catch (error) {
      setFormStatus({ type: 'error', message: 'Oops! There was a problem submitting your form' });
    }
  };

  return (
    <main className="min-h-screen bg-white">
      <section className="relative py-20 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-400/20 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center z-10">
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8 }}>
            <div className="w-20 h-1 bg-yellow-400 rounded-full mx-auto mb-6"></div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Let's <span className="text-yellow-400">Connect</span></h1>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">We're here to help bring your vision to life</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 px-4 -mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div initial={{ x: -50, opacity: 0 }} whileInView={{ x: 0, opacity: 1 }} className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-blue-800 mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Name</label>
                    <input type="text" name="name" required className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl focus:outline-none focus:border-blue-500 transition-colors" placeholder="John Doe" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Email</label>
                    <input type="email" name="email" required className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl focus:outline-none focus:border-blue-500 transition-colors" placeholder="john@example.com" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
                  <input type="text" name="subject" required className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl focus:outline-none focus:border-blue-500 transition-colors" placeholder="How can we help?" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
                  <textarea name="message" rows="5" required className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl focus:outline-none focus:border-blue-500 transition-colors resize-none" placeholder="Tell us about your project..."></textarea>
                </div>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="w-full py-4 bg-blue-800 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                  <Send size={18} /> Send Message
                </motion.button>
                {formStatus && (
                  <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`text-center font-medium ${formStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {formStatus.message}
                  </motion.p>
                )}
              </form>
            </motion.div>

            <motion.div initial={{ x: 50, opacity: 0 }} whileInView={{ x: 0, opacity: 1 }} className="space-y-6">
              <div className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-800 rounded-xl"><MapPin className="w-6 h-6 text-white" /></div>
                  <div>
                    <h3 className="font-semibold text-lg">Our Office</h3>
                    <p className="text-gray-600">Margoliz Plaza, Cnr Harare St & Speke Ave, Harare</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-800 rounded-xl"><Mail className="w-6 h-6 text-white" /></div>
                  <div>
                    <h3 className="font-semibold text-lg">Email Us</h3>
                    <a href="mailto:info@buildfast.co.zw" className="text-gray-600 hover:text-blue-800 transition-colors">info@buildfast.co.zw</a>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-800 rounded-xl"><Phone className="w-6 h-6 text-white" /></div>
                  <div>
                    <h3 className="font-semibold text-lg">Call Us</h3>
                    <a href="tel:+263776347735" className="text-gray-600 hover:text-blue-800 transition-colors">+263 776 347 735</a>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-blue-800 rounded-xl"><Clock className="w-6 h-6 text-white" /></div>
                  <h3 className="font-semibold text-lg">Office Hours</h3>
                </div>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-green-500 rounded-full"></span> Mon - Fri: 9:00 AM - 5:00 PM</li>
                  <li className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-yellow-500 rounded-full"></span> Saturday: 10:00 AM - 2:00 PM</li>
                  <li className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-red-500 rounded-full"></span> Sunday: Closed</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ y: 50, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <iframe
              title="Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3798.225517258067!2d31.047399015353085!3d-17.828917987827373!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1931a4f3f06beccd%3A0x1a82d14d840b163d!2sHarare%20St%20%26%20Speke%20Ave%2C%20Harare%2C%20Zimbabwe!5e0!3m2!1sen!2s!4v1645784892834!5m2!1sen!2s"
              className="w-full h-[400px]"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            />
          </motion.div>
        </div>
      </section>
    </main>
  );
};
